import { musicPlatform } from 'fitbeat.gym-core/config';
import { CardioOrResistance } from 'fitbeat.models';
import produce from 'immer';
import { Image, Platform } from 'react-native';
import { OPEN_HOME_WORKOUT_REQUEST } from '../../actions';
import {
  BACK_FROM_MUSIC_SOURCE_SELECT_SCREEN_PRESSED,
  BACK_FROM_PLAYLIST_SELECT_SCREEN_PRESSED,
  CHANGE_PLAYLIST_PRESSED,
  CLEAN_GENERIC_MUSIC,
  CLEAN_MUSIC_STORE,
  CONNECTION_STATE_CHANGE,
  FETCH_FB_PLAYLISTS_SUCCESS,
  FETCH_PLAYLISTS_SUCCESS,
  FETCH_RECOMMENDED_PLAYLISTS_SUCCESS,
  FETCH_SONGS_SUCCESS,
  FETCH_USER_PLAYLISTS_SUCCESS,
  FETCH_USER_PROFILE_FAILURE,
  FETCH_USER_PROFILE_SUCCESS,
  GENERIC_MUSIC_ERROR,
  GENERIC_MUSIC_LOADED_SUCCESS,
  GENERIC_MUSIC_PAUSE,
  GENERIC_MUSIC_PLAY,
  GENERIC_MUSIC_SKIP_TO_NEXT,
  GENERIC_MUSIC_SKIP_TO_PREVIOUS,
  IGenericMusicTrack,
  LOAD_SPOTIFY_ONBOARDING,
  MUSIC_AUTOPLAY_INITIALISED,
  MUSIC_AUTOPLAY_INITIALISING,
  MUSIC_AUTOPLAY_SETUP,
  MUSIC_PLAYER_SELECTED,
  MusicActionTypes,
  PLAYER_ERROR,
  PLAYER_PAUSED,
  PLAYER_PLAY,
  PLAYER_SHUFFLE_OFF,
  PLAYER_SHUFFLE_ON,
  PLAYER_SKIP_BACK,
  PLAYER_SKIP_FORWARD,
  PLAYER_STATE_CHANGE,
  SAME_MUSIC_SOURCE_SELECTED,
  SELECT_MUSIC_SOURCE_BUTTON_PRESS,
  SELECT_TRACK,
  SELECTED_PLAYLIST,
  SET_MUSIC_PLAYER_TO_AUTOPLAY,
  SPOTIFY_ERROR,
  SPOTIFY_ONBOARDING_CLOSED,
} from './actions';
import { IPlayerState, IPlaylist, ISong, MusicInitialiseStates } from './interfaces';
import { MusicScreens } from './MusicSetup';

export interface IMusicState {
  musicPlayerToAutoplay: musicPlatform | null;
  playerState: IPlayerState | null;
  isConnected: boolean;
  error: Error | null;
  selectedPlaylist: IPlaylist | null;
  musicPlayer: musicPlatform | null;
  playlistsLoading: boolean;
  songsLoading: boolean;
  songs: ISong[];
  premiumUser: boolean;
  userPlaylists: IPlaylist[];
  fbPlaylists: IPlaylist[];
  recommendedPlaylists: IPlaylist[];
  canFetchUserPlaylistsOnPlaylistScreenFocus: boolean;
  authenticationFailed: boolean;
  authenticationFailMessage?: string;
  authenticationFailedPlayer?: musicPlatform;
  genericMusicLoadedForCardioOrResistance: CardioOrResistance | null;
  genericMusicPlaylist: IGenericMusicTrack[];
  currentGenericMusicTrackIndex: number;
  genericMusicIsPaused: boolean;
  displayMusicScreen: MusicScreens;
  defaultBuildBurnPlaylistIds: string[];
  displayName: string;
  screenFromWhichSpotifyOnboardingWasLaunched: MusicScreens;
  musicInitialiseState: MusicInitialiseStates;
}

const initialMusicState: IMusicState = {
  musicPlayerToAutoplay: null,
  playerState: null,
  isConnected: false,
  error: null,
  selectedPlaylist: null,
  musicPlayer: null,
  playlistsLoading: false,
  songs: [],
  songsLoading: false,
  premiumUser: false,
  userPlaylists: [],
  fbPlaylists: [],
  recommendedPlaylists: [],
  canFetchUserPlaylistsOnPlaylistScreenFocus: false,
  authenticationFailed: false,
  authenticationFailMessage: undefined,
  authenticationFailedPlayer: undefined,
  genericMusicLoadedForCardioOrResistance: null,
  genericMusicPlaylist: [],
  currentGenericMusicTrackIndex: -1,
  genericMusicIsPaused: true,
  displayMusicScreen: MusicScreens.MusicSourceSelect,
  defaultBuildBurnPlaylistIds: [],
  displayName: '',
  screenFromWhichSpotifyOnboardingWasLaunched: MusicScreens.GenericMusicPlayer,
  musicInitialiseState: 'not-started',
};

export const musicReducer = produce(
  (draft: IMusicState = initialMusicState, action: MusicActionTypes): IMusicState => {
    switch (action.type) {
      case SET_MUSIC_PLAYER_TO_AUTOPLAY:
        setMusicPlayerToAutoplay(draft, action.payload.musicPlayer);
        break;
      case OPEN_HOME_WORKOUT_REQUEST:
        draft.musicInitialiseState = 'not-started';
        break;
      case MUSIC_AUTOPLAY_SETUP:
        draft.musicInitialiseState = 'not-started';
        setMusicPlayerToAutoplay(draft, action.payload.musicPlayer);
        break;
      case MUSIC_AUTOPLAY_INITIALISING:
        draft.musicInitialiseState = 'started';
        break;
      case MUSIC_AUTOPLAY_INITIALISED:
        draft.musicInitialiseState = 'done';
        break;
      case PLAYER_STATE_CHANGE:
        draft.playerState = action.payload.playerState;
        break;
      case SELECT_TRACK:
        break;
      case PLAYER_SKIP_BACK:
        break;
      case PLAYER_SKIP_FORWARD:
        break;
      case PLAYER_SHUFFLE_ON:
        break;
      case PLAYER_SHUFFLE_OFF:
        break;
      case PLAYER_PLAY:
        break;
      case PLAYER_PAUSED:
        break;
      case CONNECTION_STATE_CHANGE:
        draft.isConnected = action.payload.isConnected;
        draft.playerState = action.payload.playerState;
        break;
      case PLAYER_ERROR:
        draft.error = action.payload;
        draft.playlistsLoading = false;
        draft.songsLoading = false;
        break;
      case SELECTED_PLAYLIST:
        draft.selectedPlaylist = action.payload.playlist;
        draft.songsLoading = true;
        draft.displayMusicScreen = MusicScreens.MusicPlayer;
        break;
      case FETCH_SONGS_SUCCESS:
        draft.songs = action.payload;
        draft.songsLoading = false;
        break;
      case FETCH_PLAYLISTS_SUCCESS:
        draft.playlistsLoading = false;
        break;
      case MUSIC_PLAYER_SELECTED:
        musicPlayerSelected(draft, action);
        const hasGenericTracksToPlay = draft.genericMusicPlaylist.length > 0;
        if (action.payload === musicPlatform.FitbeatMusic && action.autostart && hasGenericTracksToPlay) {
          draft.currentGenericMusicTrackIndex = 0;
          draft.genericMusicIsPaused = false;
        }
        break;
      case FETCH_USER_PLAYLISTS_SUCCESS:
        draft.userPlaylists = action.payload;
        draft.canFetchUserPlaylistsOnPlaylistScreenFocus = true;
        break;
      case FETCH_FB_PLAYLISTS_SUCCESS:
        action.payload.playlists.map((playlist: IPlaylist) =>
          playlist.image = Image.resolveAssetSource(require('../../../assets/images/fitbeat-music-star.jpg')));
        draft.fbPlaylists = action.payload.playlists;
        draft.defaultBuildBurnPlaylistIds = action.payload.defaultBuildBurnPlaylistIds;
        break;
      case FETCH_RECOMMENDED_PLAYLISTS_SUCCESS:
        draft.recommendedPlaylists = action.payload;
        break;
      case FETCH_USER_PROFILE_SUCCESS:
        draft.premiumUser = action.payload.product === 'premium';
        draft.authenticationFailed = false;
        draft.authenticationFailMessage = undefined;
        draft.displayName = action.payload.displayName;
        delete draft.authenticationFailMessage;
        break;
      case FETCH_USER_PROFILE_FAILURE:
        draft.musicPlayer = null;
        draft.authenticationFailed = true;
        if (action.payload && action.payload.authenticationFailMessage) {
          draft.authenticationFailMessage = action.payload.authenticationFailMessage;
        }
        draft.authenticationFailedPlayer = action.payload?.authenticationFailedPlayer;
        draft.songsLoading = false;
        draft.displayMusicScreen = MusicScreens.MusicSourceSelect;
        break;
      case SPOTIFY_ERROR:
        musicPlayerSelected(draft, action);
        draft.musicPlayer = null;
        draft.authenticationFailed = true;
        draft.songsLoading = false;
        draft.displayMusicScreen = MusicScreens.MusicSourceSelect;
        break;
      case CLEAN_MUSIC_STORE:
        draft.playerState = null;
        draft.recommendedPlaylists = [];
        draft.userPlaylists = [];
        draft.fbPlaylists = [];
        draft.displayName = '';
        draft.authenticationFailed = false;
        draft.authenticationFailMessage = undefined;
        draft.authenticationFailedPlayer = undefined;
        draft.musicPlayer = null;
        draft.displayMusicScreen = MusicScreens.MusicSourceSelect;
        break;
      case SELECT_MUSIC_SOURCE_BUTTON_PRESS:
        draft.displayMusicScreen = MusicScreens.MusicSourceSelect;
        break;
      case SAME_MUSIC_SOURCE_SELECTED:
        if (draft.musicPlayer === musicPlatform.Spotify) {
          draft.displayMusicScreen = MusicScreens.MusicPlayer;
        }
        if (draft.musicPlayer === musicPlatform.AppleMusic && Platform.OS === 'ios') {
          draft.displayMusicScreen = MusicScreens.MusicPlayer;
        }
        if (draft.musicPlayer === musicPlatform.FitbeatMusic) {
          draft.displayMusicScreen = MusicScreens.GenericMusicPlayer;
        }
        break;
      case GENERIC_MUSIC_LOADED_SUCCESS:
        draft.genericMusicLoadedForCardioOrResistance = action.payload.cardioOrResistance;
        draft.genericMusicPlaylist = action.payload.tracks;
        const isPlayingMusic = draft.currentGenericMusicTrackIndex !== -1 && draft.genericMusicPlaylist.length > 0;
        if (isPlayingMusic) {
          draft.currentGenericMusicTrackIndex = 0;
        }
        break;
      case GENERIC_MUSIC_PAUSE:
        draft.genericMusicIsPaused = true;
        break;
      case GENERIC_MUSIC_PLAY:
        draft.genericMusicIsPaused = false;
        if (draft.currentGenericMusicTrackIndex === -1 && draft.genericMusicPlaylist.length !== -1) {
          draft.currentGenericMusicTrackIndex = 0;
        }
        break;
      case GENERIC_MUSIC_SKIP_TO_NEXT:
        genericMusicSkipNext(draft);
        break;
      case GENERIC_MUSIC_SKIP_TO_PREVIOUS:
        if (draft.genericMusicPlaylist.length === 0) {
          break;
        }
        if (draft.currentGenericMusicTrackIndex === 0) {
          draft.currentGenericMusicTrackIndex = draft.genericMusicPlaylist.length - 1;
        } else {
          draft.currentGenericMusicTrackIndex--;
        }
        draft.genericMusicIsPaused = false;
        break;
      case CLEAN_GENERIC_MUSIC:
        draft.currentGenericMusicTrackIndex = -1;
        draft.genericMusicIsPaused = true;
        draft.genericMusicPlaylist = [];
        draft.genericMusicLoadedForCardioOrResistance = null;
        break;
      case GENERIC_MUSIC_ERROR:
        const unknownerrorErrorCode = '11819';
        const shouldAutoRecover = action.payload.error && action.payload.error.includes(unknownerrorErrorCode);
        if (shouldAutoRecover) {
          genericMusicSkipNext(draft);
          break;
        }
        draft.genericMusicIsPaused = true;
        draft.currentGenericMusicTrackIndex = -1;
        break;
      case CHANGE_PLAYLIST_PRESSED:
        draft.displayMusicScreen = MusicScreens.PlaylistSelect;
        break;
      case BACK_FROM_PLAYLIST_SELECT_SCREEN_PRESSED:
        draft.displayMusicScreen = MusicScreens.MusicPlayer;
        break;
      case BACK_FROM_MUSIC_SOURCE_SELECT_SCREEN_PRESSED:
        if (draft.musicPlayer === musicPlatform.Spotify || draft.musicPlayer === musicPlatform.AppleMusic) {
          draft.displayMusicScreen = MusicScreens.MusicPlayer;
        }
        if (draft.musicPlayer === musicPlatform.FitbeatMusic) {
          draft.displayMusicScreen = MusicScreens.GenericMusicPlayer;
        }
        break;
      case LOAD_SPOTIFY_ONBOARDING:
        draft.screenFromWhichSpotifyOnboardingWasLaunched =  draft.displayMusicScreen;
        draft.displayMusicScreen = MusicScreens.MusicSpotifyOnBoarding;
        break;
      case SPOTIFY_ONBOARDING_CLOSED:
        draft.displayMusicScreen = draft.screenFromWhichSpotifyOnboardingWasLaunched;
        break;
    }
    return draft;

  });
function setMusicPlayerToAutoplay(draft: IMusicState, musicPlayer: musicPlatform) {
    if (musicPlayer !== musicPlatform.FitbeatMusic) {
      draft.musicPlayerToAutoplay = musicPlayer;
    } else {
      draft.musicPlayerToAutoplay = null;
    }
}
function genericMusicSkipNext(draft: IMusicState) {
  if (draft.genericMusicPlaylist.length === 0) {
    return;
  }
  if (draft.currentGenericMusicTrackIndex === draft.genericMusicPlaylist.length - 1) {
    draft.currentGenericMusicTrackIndex = 0;
  } else {
    draft.currentGenericMusicTrackIndex++;
  }
  draft.genericMusicIsPaused = false;
}

function musicPlayerSelected(draft: IMusicState, action: any) {
  draft.musicPlayer = action.payload;
  draft.songsLoading = true;
  if (draft.userPlaylists.length === 0) {
    draft.playlistsLoading = true;
  }
  if (action.payload !== musicPlatform.Spotify) {
    draft.selectedPlaylist = null;
  }

  if (action.payload === musicPlatform.Spotify) {
    draft.displayMusicScreen = MusicScreens.MusicPlayer;
  }

  if (action.payload === musicPlatform.FitbeatMusic) {
    draft.displayMusicScreen = MusicScreens.GenericMusicPlayer;
  }

  if (action.payload === musicPlatform.AppleMusic) {
    if (Platform.OS === 'ios') {
      draft.displayMusicScreen = MusicScreens.MusicPlayer;
    } else {
      draft.displayMusicScreen = MusicScreens.MusicSourceSelect;
    }
  }
}
